import api from "../axios";

/**
 * HMS document access rules, read requirements and «Rutiner per ansatt».
 * API contract: promang-api `Api::V1::Hse::DocumentComplianceController` and
 * `HseDocumentsController#access_info|update_access|postpone_read_requirement`.
 * Known validation failures return 422 `{ code, message, details }`.
 */

export const getComplianceEmployees = async () => {
  const response = await api.get("/hse/document_compliance/employees");
  return response.data;
};

export const getEmployeeRoutines = async (userId) => {
  const response = await api.get(`/hse/document_compliance/employees/${userId}`);
  return response.data;
};

/** Add (grant=true) or remove (grant=false) a person as manual reader of documents. */
export const updateEmployeeDocumentAccess = async (userId, documentIds, grant) => {
  const response = await api.post(`/hse/document_compliance/employees/${userId}/access`, {
    document_ids: documentIds,
    grant,
  });
  return response.data;
};

/** Apply «Tilgang og lesekrav» to many documents. Omitted settings keys stay unchanged. */
export const updateDocumentsComplianceSettings = async (documentIds, settings) => {
  const response = await api.post("/hse/document_compliance/documents/settings", {
    document_ids: documentIds,
    settings,
  });
  return response.data;
};

/**
 * The signed-in employee's own required reading: `{ enabled, count, overdue_count, due_soon_count,
 * next_due_at, handbook_url, items: [{ document_id, title, document_number, due_at, overdue, due_soon,
 * postponements_left, url }] }`. Feeds the HMS reminder banner and the front page.
 */
export const getMyReadRequirements = async () => {
  const response = await api.get("/hse/document_compliance/me");
  return response.data;
};

/** Fired after the reader confirms or postpones, so banner and front page refresh at once. */
export const HSE_READ_CHANGED_EVENT = "promang:hse-read-changed";

export const notifyHseReadChanged = () => {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(HSE_READ_CHANGED_EVENT));
};

export const getComplianceOptions = async () => {
  const response = await api.get("/hse/document_compliance/options");
  return response.data;
};

/** The reader postpones their own deadline. */
export const postponeDocumentReadRequirement = async (documentId, days, lockVersion) => {
  const response = await api.post(`/hse_documents/${documentId}/read_requirement/postpone`, {
    days,
    lock_version: lockVersion,
  });
  return response.data;
};

/** Server-provided, user-facing message for a known compliance error (never a generic toast). */
export const complianceErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  return data?.message || data?.errors?.[0] || fallback;
};

export const complianceErrorCode = (error) => error?.response?.data?.code || null;
